import { LoadingOutlined } from '@ant-design/icons';
import { Modal } from 'antd';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';

import { getCurrentAdvertiser } from '../../modules/organization/organizationSlice';
import { usePerformance } from '../../modules/performance/usePerformance';
import { localStorageService } from '../../services/localStorage';
import { DSP_ADFORM } from '../../utils/constants';
import { Indicator } from '../common';

type AdPreviewModalProps = {
  creativeId: string;
  isOpen: boolean;
  onClose: () => void;
};

export const AdPreviewModal = ({ creativeId, isOpen, onClose }: AdPreviewModalProps) => {
  const { getCreativeMetadata, creativeMetadata, isCreativeMetadataLoading, errorText } =
    usePerformance();

  const PLACEHOLDER_WIDTH = 600;
  const PLACEHOLDER_HEIGHT = 400;
  const PADDING = 24;
  const MAX_VIDEO_WIDTH = 900;
  const MAX_VIDEO_HEIGHT = 600;

  const [showContent, setShowContent] = useState(false);
  const previousCreativeIdRef = useRef<string | null>(null);

  const advertiserXandrId = useSelector(getCurrentAdvertiser)?.xandrId;
  const isDemoAccount = advertiserXandrId == 1 || advertiserXandrId == 2 || advertiserXandrId == 3;
  const currentDsp = localStorageService.getItem<string>('currentDsp') || 'XANDR';

  useEffect(() => {
    if (!isOpen) {
      setShowContent(false);

      return;
    }

    const isSameCreative = previousCreativeIdRef.current === creativeId;
    const isValidMetadata =
      creativeMetadata &&
      creativeMetadata.width &&
      creativeMetadata.height &&
      creativeMetadata.type;

    if (creativeId && previousCreativeIdRef.current !== creativeId) {
      setShowContent(false);
      getCreativeMetadata(creativeId);
      previousCreativeIdRef.current = creativeId;
    } else if (isSameCreative && isValidMetadata) {
      setShowContent(true);
    }
  }, [isOpen, creativeId, creativeMetadata, getCreativeMetadata]);

  useEffect(() => {
    if (!creativeMetadata) return;

    const timeout = setTimeout(() => {
      setShowContent(true);
    }, 325);

    return () => clearTimeout(timeout);
  }, [creativeMetadata]);

  const isReady = useMemo(() => {
    return (
      !isCreativeMetadataLoading &&
      errorText == null &&
      showContent &&
      creativeMetadata?.width &&
      creativeMetadata?.height &&
      creativeMetadata?.type
    );
  }, [isCreativeMetadataLoading, errorText, showContent, creativeMetadata]);

  const scaled = useMemo(() => {
    if (
      isCreativeMetadataLoading ||
      errorText != null ||
      !creativeMetadata?.width ||
      !creativeMetadata?.height
    ) {
      return {
        width: PLACEHOLDER_WIDTH,
        height: PLACEHOLDER_HEIGHT,
        paddedWidth: PLACEHOLDER_WIDTH + PADDING * 2,
      };
    }

    const { width, height, type } = creativeMetadata;

    if (type === 'video') {
      const ratio = Math.min(1, MAX_VIDEO_WIDTH / width, MAX_VIDEO_HEIGHT / height);
      const w = Math.round(width * ratio);
      const h = Math.round(height * ratio);

      return {
        width: w,
        height: h,
        paddedWidth: w + PADDING * 2,
      };
    }

    return {
      width,
      height,
      paddedWidth: width + PADDING * 2,
    };
  }, [isCreativeMetadataLoading, creativeMetadata, errorText]);

  const renderContent = () => {
    if (isCreativeMetadataLoading) {
      return <LoadingOutlined style={{ fontSize: '500px' }} />;
    }

    // ADFORM DSP specific handling for standard/image creatives
    if (
      isReady &&
      currentDsp === DSP_ADFORM &&
      creativeMetadata?.type &&
      (creativeMetadata.type === 'standard' || creativeMetadata.type === 'image')
    ) {
      return (
        <img
          src={creativeMetadata.imageData || creativeMetadata.src}
          className="border-0"
          width={scaled.width}
          height={scaled.height}
          alt="Creative Preview"
        />
      );
    }

    // ADFORM DSP specific handling for video creatives
    if (isReady && currentDsp === DSP_ADFORM && creativeMetadata?.type === 'video') {
      return (
        <video width={scaled.width} height={scaled.height} autoPlay controls>
          <source src={creativeMetadata.vastUrl} type="video/mp4" />
          Your browser does not support the video tag.
        </video>
      );
    }

    // ADFORM DSP specific handling for third-party (tag-served) creatives.
    // The creative is an HTML/script snippet carried in `src`; render it in a
    // sandboxed iframe so the third-party JS is isolated from the app.
    if (isReady && currentDsp === DSP_ADFORM && creativeMetadata?.type === 'thirdParty') {
      return (
        <iframe
          srcDoc={creativeMetadata.src}
          className="border-0"
          width={scaled.width}
          height={scaled.height}
          sandbox="allow-scripts"
          referrerPolicy="no-referrer"
          title="Creative Preview"
        />
      );
    }

    // ADFORM DSP specific handling for HTML5 asset banners. Unlike third-party tags,
    // `src` is a hosted creative URL (the asset's FileUrl), so load it directly in a
    // sandboxed iframe. allow-same-origin lets the creative fetch its relative assets.
    if (isReady && currentDsp === DSP_ADFORM && creativeMetadata?.type === 'html') {
      return (
        <iframe
          src={creativeMetadata.src}
          className="border-0"
          width={scaled.width}
          height={scaled.height}
          scrolling="no"
          sandbox="allow-scripts allow-same-origin allow-popups"
          title="Creative Preview"
        />
      );
    }

    if (isReady && creativeMetadata?.type === 'video') {
      if (isDemoAccount) {
        return (
          <video
            src={
              'https://851725435445-us-east-1-dev-media-store.s3.us-east-1.amazonaws.com/cc36eaf1-ffba-4d54-bb9f-6777f27cdd24_1920_1080_7000k.mp4'
            }
            className="border-0"
            width={scaled.width}
            height={scaled.height + 5}
            autoPlay
            controls
          />
        );
      } else
        return (
          <video width={scaled.width} height={scaled.height} autoPlay controls>
            <source src={creativeMetadata.videoUrl} type="video/mp4" />
            Your browser does not support the video tag.
          </video>
        );
    }

    if (isReady && creativeMetadata?.type === 'standard') {
      if (isDemoAccount) {
        return (
          <img
            src={
              'https://851725435445-us-east-1-dev-media-store.s3.us-east-1.amazonaws.com/demoimg.jpg'
            }
            className="border-0"
            width={scaled.width}
            height={scaled.height + 5}
            alt="Creative Preview"
          />
        );
      } else
        return (
          <iframe
            src={`https://creative-preview-an.com/direct/creative/${creativeId}`}
            className="border-0"
            width={scaled.width}
            height={scaled.height + 5}
            allowFullScreen
            allow="autoplay"
          />
        );
    }

    if (errorText != null) {
      return (
        <Indicator variant="danger" size="lg" className="max-w-[260px]">
          Creative preview not found
        </Indicator>
      );
    }

    return null;
  };

  return (
    <Modal
      title={'Ad Preview'}
      open={isOpen}
      onCancel={onClose}
      footer={null}
      centered
      className="ctv-ad-preview-modal"
      width={scaled.paddedWidth}
      destroyOnClose
    >
      <div className="ctv-ad-preview-container" style={{ height: scaled.height }}>
        {renderContent()}
      </div>
    </Modal>
  );
};
